import { useNavigate } from "@/lib/router-compat";
import { useCryptoData } from "@/hooks/useCryptoData";
import { cn } from "@/lib/utils";
import { cdnImage } from "@/lib/images";

/**
 * Global crypto price ticker rendered above the app navbar.
 * Marquee animation is defined inline via a <style> tag so it stays
 * self-contained without touching tailwind.config.
 */
const CryptoTicker = () => {
  const navigate = useNavigate();
  const { tokens, loading } = useCryptoData({
    limit: 20,
    sortBy: "market_cap_rank",
    sortDirection: "asc",
    enableRealtime: false,
  });

  if (loading || tokens.length === 0) {
    return (
      <div className="fixed top-0 left-0 right-0 h-8 bg-neutral-950 border-b border-neutral-800 z-[60]" aria-hidden />
    );
  }

  // Duplicate the list so the marquee loops seamlessly.
  const items = [...tokens, ...tokens];

  return (
    <div className="fixed top-0 left-0 right-0 h-8 bg-neutral-950 text-neutral-100 border-b border-neutral-800 overflow-hidden group z-[60]">
      <style>{`
        @keyframes crypto-ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .crypto-ticker-track {
          animation: crypto-ticker-scroll 90s linear infinite;
          will-change: transform;
        }
        .crypto-ticker-wrap:hover .crypto-ticker-track {
          animation-play-state: paused;
        }
      `}</style>
      <div className="crypto-ticker-wrap h-full flex items-center">
        <div className="crypto-ticker-track flex items-center gap-6 whitespace-nowrap px-4">
          {items.map((token, idx) => {
            const change = token.price_change_percentage_24h ?? 0;
            const up = change >= 0;
            const price = token.current_price ?? 0;
            const formatted =
              price >= 1
                ? price.toLocaleString(undefined, { maximumFractionDigits: 2 })
                : price.toPrecision(3);
            return (
              <button
                key={`${token.id}-${idx}`}
                onClick={() => navigate(`/markets/${token.id}`)}
                className="flex items-center gap-1.5 text-xs font-medium hover:text-white transition-colors"
              >
                {token.logo_url && (
                  <img
                    src={cdnImage(token.logo_url, { width: 16, height: 16, fit: "contain" })}
                    alt=""
                    className="w-3.5 h-3.5 rounded-full"
                    loading="lazy"
                  />
                )}
                <span className="uppercase tracking-wide text-neutral-200">
                  {token.symbol}
                </span>
                <span className="tabular-nums text-neutral-100">${formatted}</span>
                <span
                  className={cn(
                    "tabular-nums",
                    up ? "text-success" : "text-rose-400"
                  )}
                >
                  {up ? "+" : ""}
                  {change.toFixed(2)}%
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CryptoTicker;
